import React, { useState, useEffect } from 'react';
import { PasslyTable } from '@app/components';
import { infiniteScrollItemsFetchLimit, useDebounce } from '@app/helpers';
import { Order, OrderType, ISelectIdentities, IActiveFilter, FilterEnum } from '@app/models';
import { Grid, Box } from '@material-ui/core';
import { ModalActionsV2, ModalV2, PasslyTabs, PasslyTab } from '@app/componentsV2';
import { useTypedTranslation } from '@app/hooks';
import { selectIdentitiesService } from '@app/services';
import _ from 'lodash';

interface IProps {
    tabs: ISelectIdentities[];
    open: boolean;
    closeCallback: Function;
    fixedFooter: boolean;
}

const SelectIdentitiesV2: React.SFC<IProps> = ({ tabs, open, closeCallback, fixedFooter }) => {
    const { translation } = useTypedTranslation();

    const [rows, setRows] = useState<any[]>([]);
    const [rowsCount, setRowsCount] = useState(0);
    const [reset, setReset] = useState(0);
    const [order, setOrder] = useState<OrderType>(Order.asc);
    const [orderBy, setOrderBy] = useState<string>(tabs[0].columns[0].id);
    const [loading, setLoading] = useState(true);
    const [initialLoad, setInitalLoad] = useState(true);
    const [selectedCount, setSelectedCount] = useState(0);
    const [selectedTab, setSelectedTab] = useState(0);
    const [searchField, setSearchField] = useState('');
    const [activeFilter, setActiveFilter] = useState<IActiveFilter[]>([]);
    const [expandQuery, setExpandQuery] = useState<string | undefined>(undefined);

    const debouncedSearch = useDebounce(searchField.trim(), 500);

    useEffect(() => {
        getExpandQuery();
    }, []);

    useEffect(() => {
        let selected = 0;

        rows.forEach((row) => {
            if (row.checked) {
                selected++;
            }
        });

        setSelectedCount(selected);
    }, [rows]);

    useEffect(() => {
        if (!initialLoad) {
            resetData();
        }
    }, [order, orderBy, debouncedSearch]);

    useEffect(() => {
        if (!initialLoad) {
            setOrder(Order.asc);
            setOrderBy(tabs[selectedTab].columns[0].id);
            getExpandQuery();
            resetData();
        }
    }, [selectedTab]);

    const resetData = () => {
        if (debouncedSearch !== '') {
            getFilter();
        } else {
            setActiveFilter([]);
        }

        setLoading(true);

        const selected = rows.filter((el: any) => {
            return el.checked === true;
        });

        setRows(selected);
        setRowsCount(selected.length);
        setSelectedCount(selected.length);
        setReset(reset + 1);
    };

    const getFilter = () => {
        let filterableColumns: string[] = [];
        tabs[selectedTab].columns.forEach((column) => {
            if (column.searchable) {
                filterableColumns.push('tolower(' + column.id + ')');
            }
        });

        if (filterableColumns.length > 0) {
            const filterBy = filterableColumns.join(',');
            setActiveFilter([{ name: FilterEnum.contains, value: debouncedSearch.toLowerCase(), filterBy: filterBy, label: '' }]);
        } else {
            setActiveFilter([]);
        }
    };

    const getExpandQuery = () => {
        let expandableQuery: string[] = [];
        tabs[selectedTab].columns.forEach((column) => {
            if (column.expandAttribute !== undefined && column.expandAttribute !== '') {
                if (column.countable) {
                    expandableQuery.push(`${column.expandAttribute}($count=true;$top=0)`);
                } else {
                    expandableQuery.push(column.id);
                }
            }
        });

        if (expandableQuery.length > 0) {
            setExpandQuery(expandableQuery.join(','));
        } else {
            setExpandQuery(undefined);
        }
    };

    const fetchMoreData = (skipCount: number, isReset: boolean) => {
        if (initialLoad) {
            setInitalLoad(false);
        }

        if (isReset) {
            setLoading(true);
        }

        selectIdentitiesService
            .getData(
                tabs[selectedTab].fetchApiUrl,
                order,
                orderBy,
                infiniteScrollItemsFetchLimit,
                skipCount,
                isReset,
                tabs[selectedTab].orgFilterId,
                tabs[selectedTab].onFetchApiError,
                activeFilter,
                expandQuery
            )
            .then((response: any) => {
                if (isReset) {
                    setRowsCount(response.data['@odata.count']);
                }

                let fetchedRows = response.data.value;
                let newRows: any[] = [];

                fetchedRows.forEach((row: any) => {
                    let selected: any = _.find(rows, { Id: row.Id });

                    if (selected && debouncedSearch === '') {
                        // IF THERE IS A FILTER THEN THE SELECTED SECTION WILL NOT CHANGE BECAUSE RESULTS MIGHT NOT BE FOUND IN THE SEARCH, BUT
                        // SELECTED LIST RESULTS STAY THE SAME
                        // this is a new sort so we need to handle it in the "Selected" area as well
                        // since the results come back already sorted, we just need to remove it from the saved rows and push it back in its place
                        newRows.push(selected);

                        const index = rows.indexOf(selected);
                        if (index > -1) {
                            rows.splice(index, 1);
                        }
                    }

                    if (!selected) {
                        row.checked = false;
                        newRows.push(row);
                    }
                });

                if (newRows.length > 0) {
                    setRows(rows.concat(newRows));
                }

                setLoading(false);
            });
    };

    const createSortHandler = (property: string) => () => {
        const ascDesc: OrderType = order === Order.asc ? Order.desc : Order.asc;
        setOrder(ascDesc);
        setOrderBy(property);
    };

    const handleTabChange = (event: React.ChangeEvent<{}>, newValue: number) => {
        setSelectedTab(newValue);
    };

    const handlePrimaryAction = () => {
        let checkedIds: string[] = [];
        rows.forEach((row: any) => {
            if (row.checked) {
                checkedIds.push(row.Id);
            }
        });
        tabs[selectedTab].primaryButtonCallback(checkedIds);
    };


    return (
        <ModalV2
            showModal={open}
            showModalCallback={() => closeCallback(false)}
            headerTitle={tabs[selectedTab].dialogHeader}
            fixedFooter={fixedFooter}
            form={
                <>
                    <div className='modal__select-identities'>
                        {tabs.length > 1 && (
                            <Box m={1} ml={2}>
                                <Grid container direction='row' alignItems='center' justify='flex-start' spacing={2}>
                                    <Grid item>
                                        <PasslyTabs style={{ minHeight: 28 }} value={selectedTab} onChange={handleTabChange}>
                                            {tabs.map((tab) => (
                                                <PasslyTab label={tab.selectedLabel} />
                                            ))}
                                        </PasslyTabs>
                                    </Grid>
                                </Grid>
                            </Box>
                        )}
                        <div className='modal__select-identities-table'>
                            <PasslyTable
                                rows={rows}
                                setRows={setRows}
                                order={order}
                                orderBy={orderBy}
                                searchValue={searchField}
                                setSearchValue={setSearchField}
                                handleOrderChange={createSortHandler}
                                headerItems={tabs[selectedTab].columns}
                                rowsCount={rowsCount}
                                onFetch={fetchMoreData}
                                withCheckboxes={true}
                                loadingData={loading}
                                resetLocal={reset}
                            />
                        </div>
                    </div>

                    <div className='modal__modal-actions'>
                        <ModalActionsV2
                            primaryBtn={tabs[selectedTab].primaryButtonLabel}
                            secondaryBtn={translation('CANCEL')}
                            onClickPrimary={handlePrimaryAction}
                            onClickSecondary={() => closeCallback(false)}
                            disabled={selectedCount === 0}
                        />
                    </div>
                </>
            }
        />
    );
};

export default SelectIdentitiesV2;
